import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  getCalendarInterval,
  getLabelsForCurrentMonth,
  getLabelsForCurrentWeek,
  getLabelsForDateRange,
  getLabelsForLast12Months,
  getLabelsForLast30Day,
  getLabelsForLast3Months,
  getLabelsForLast6Months,
  getLabelsForLast7Days,
  getLabelsForYear,
  getUTCDayRange,
  handleSingleChange,
  isPastTime,
  isTodayDate,
  monthBasedLabelWithYear,
  pushDataByTimeLabel,
  timeLabelForCharts,
} from "./utils/utils.js";
import SelectDropdownCompo from "./selectDropdownCompo";
import dayjs from "dayjs";

const SuccessChart = ({
  successRatePercentage,
  successRateDetails,
  selectedDropdownValue,
  gmvPspData,
  handleFilterDropdown,
  currencyCode,
  legalEntity,
  refresh,
  singleStartDate,
  startDate,
  endDate,
}) => {
  let savedSelectAll = JSON.parse(localStorage.getItem("successRateOverallFilters"))?.selectAll === true ? true : false;
  let savedPropsessors = JSON.parse(localStorage.getItem("successRateOverallFilters"))?.others || [];

  const [selectAll, setSelectAll] = useState(savedSelectAll);
  const normalizeTimestamp = (timestamp) => {
    return dayjs(timestamp, ["DD/MM/YYYY HH:mm:ss Z", "DD/MM/YYYY"]).format(
      "YYYY-MM-DD"
    );
  };

  const preprocessData = (data) => {
    return data.map((item) => ({
      ...item,
      normalizedTimestamp: normalizeTimestamp(item.timestamp),
    }));
  };
  const aggregateData = (data) => {
    return data.reduce((acc, item) => {
      const key = `${item.normalizedTimestamp}-${item.pspCodeLocale}`;
      if (!acc[key]) {
        acc[key] = { ...item, successRateSum: 0, count: 0 };
      }
      acc[key].successRateSum = parseFloat(item.successRate || 0);
      acc[key].count += 1;
      return acc;
    }, {});
  };

  const colorArrayForLines = [
    { name: "Overall", color: "#2c2c2c" },
    { name: "Billdesk", color: "#F87422" },
    { name: "Adyen", color: "#36AA55" },
    { name: "Razorpay", color: "#3395FF" },
    { name: "Stripe", color: "#635BFF" },
    { name: "Triplea", color: "#151644" },
    { name: "Atome", color: "#F0FF5F" },
    { name: "2c2p", color: "#064F5C" },
    { name: "Cybersource", color: "#4074FE" },
    { name: "EazeBuzz", color: "#091B49" },
    { name: "Globalpayments", color: "#00477F" },
    { name: "Jiopay", color: "#0B2972" },
    { name: "Xendit", color: "#437FF2" },
    { name: "Worldpay", color: "#E0000B" },
    { name: "Reddot", color: "#81D20" },
    { name: "Paypal", color: "#169BD7" },
    { name: "Braintree", color: "#000000" },
    { name: "Cryptomus", color: "#000000" },
    { name: "Phonepe", color: "#5F259F" },
    { name: "Fiserv", color: "#FF6600" },
  ];

  const [selectedOptions, setSelectedOptions] = useState([]);
  const [filterData, setFilteredData] = useState([]);

  useEffect(() => {
    handleFilterDropdown(selectedOptions);
  }, [selectedOptions]);

  const uniqueOptionTypes = [
    ...new Set(gmvPspData.map((option) => option?.pspCodeLocale)),
  ];

  useEffect(() => {
    if (selectedOptions.length === 0) {
      setFilteredData([]);
    } else {
      const filteredData = successRateDetails?.filter((item) =>
        selectedOptions.includes(item?.pspCodeLocale)
      );
      setFilteredData(filteredData);
    }
  }, [selectedOptions, successRateDetails]);

  useEffect(() => {
    if (gmvPspData) {
      let selectedOptions = [];
      if (savedSelectAll) {
        setSelectAll(true);
        for (let i = 0; i < gmvPspData?.length; i++) {
          selectedOptions.push(gmvPspData[i]?.pspCodeLocale);
        }
      } else {
        let filteredGmv = gmvPspData.filter((option) =>
          savedPropsessors.includes(option?.pspCodeLocale)
        );
        for (let i = 0; i < filteredGmv?.length; i++) {
          selectedOptions.push(filteredGmv[i]?.pspCodeLocale);
        }
      }
      if (selectedOptions?.length === 0) {
        setSelectedOptions(["Overall"]);
      } else {
        setSelectedOptions(selectedOptions);
      }
    }
  }, [gmvPspData]);

  const colorDictionary = {};
  const filteredDataWithColors = filterData?.map((item) => {
    const { pspCodeLocale } = item;
    if (colorDictionary[pspCodeLocale]) {
      item.color = colorDictionary[pspCodeLocale];
    } else {
      const colorObject = colorArrayForLines.find(
        (color) => color.name.toLowerCase() === pspCodeLocale.toLowerCase()
      );
      if (colorObject) {
        item.color = colorObject.color;
        colorDictionary[pspCodeLocale] = colorObject.color;
      } else {
        item.color = "";
      }
    }
    return item;
  });

  const handleSetSessionStrorage = (selectAll, others) => {
    localStorage.setItem(
      "successRateOverallFilters",
      JSON.stringify({
        selectAll: selectAll,
        others: others,
      })
    );
  };

  const handleProcessorSelect =
    (selectedOptions, uniqueOptionTypes, selectAll) => (event) => {
      const newSelectedOptions = event.target.value;
      if (newSelectedOptions.includes("Select All")) {
        if (selectAll) {
          setSelectedOptions([]);
          setSelectAll(false);
          handleSetSessionStrorage(false, []);
        } else {
          setSelectedOptions(uniqueOptionTypes);
          setSelectAll(true);
          handleSetSessionStrorage(true, uniqueOptionTypes);
        }
      } else {
        setSelectedOptions((prevSelectedOptions) => {
          const updatedOptions = newSelectedOptions.filter(
            (value) => value !== "Select All"
          );
          const allOtherOptionsSelected =
            updatedOptions.length === uniqueOptionTypes.length &&
            !updatedOptions.includes("Select All");
          setSelectedOptions(newSelectedOptions);
          handleSetSessionStrorage(allOtherOptionsSelected, newSelectedOptions);
          setSelectAll(allOtherOptionsSelected);
          return updatedOptions;
        });
      }
    };

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const [labels, setLabels] = useState([]);
  const [dataByPSP, setDataByPSP] = useState({});

  const currentTime = new Date();
  const currentMonth = currentTime.getMonth();
  const nextMonth = currentMonth + 1;
  const currentHour = currentTime.getHours();
  const currentMinute = currentTime.getMinutes();
  const today = new Date();
  const currentYearThisYear = today.getFullYear();

  useEffect(() => {
    let newLabels = [];
    let newDataByPSP = {};

    switch (selectedDropdownValue) {
      case "yesterday":
        const getLabelForYesterday = timeLabelForCharts();
        for (let i = 0; i < getLabelForYesterday.length; i++) {
          const dataPoint = getLabelForYesterday[i];
          newLabels.push(dataPoint);
          pushDataByTimeLabel(
            newLabels,
            newDataByPSP,
            filterData,
            selectedOptions,
            dataPoint,
            currentHour,
            currentMinute,
            false,
            i === getLabelForYesterday.length - 1,
            "successRate"
          );
        }
        break;
      case "today":
        const getLabelForToday = timeLabelForCharts();
        for (let i = 0; i < getLabelForToday.length; i++) {
          const dataPoint = getLabelForToday[i];
          newLabels.push(dataPoint);

          const pastTime = isPastTime(dataPoint, currentHour, currentMinute);

          if (pastTime) {
            pushDataByTimeLabel(
              newLabels,
              newDataByPSP,
              filterData,
              selectedOptions,
              dataPoint,
              currentHour,
              currentMinute,
              true,
              i === getLabelForToday.length - 1,
              "successRate"
            );
          } else {
            selectedOptions.forEach((pspCodeLocale) => {
              if (!newDataByPSP[pspCodeLocale]) {
                newDataByPSP[pspCodeLocale] = [];
              }
              newDataByPSP[pspCodeLocale].push(null);
            });
          }
        }
        break;
      case "thisWeek":
        const getLabelForThisWeek = getLabelsForCurrentWeek();
        for (let i = 0; i < getLabelForThisWeek.length; i++) {
          const dataPoint = getLabelForThisWeek[i];
          newLabels.push(dataPoint);
        }
        const todayDate = dayjs(); // Current date

        selectedOptions.forEach((pspCodeLocale) => {
          newDataByPSP[pspCodeLocale] = newLabels.map((dateLabel) => {
            const day = dateLabel.split(" ")[0].padStart(2, "0");
            const month = monthNames.indexOf(dateLabel.split(" ")[1]) + 1;
            const year = todayDate.year(); // Get current year

            const formattedDate = dayjs(`${year}-${month}-${day}`, "YYYY-M-D");

            if (formattedDate.isAfter(todayDate, "day")) {
              return null; // Data is null for future dates
            }

            const formattedDay = formattedDate.format("DD/MM");
            const dataItem = filterData?.find(
              (item) =>
                item?.timestamp.includes(formattedDay) &&
                item?.pspCodeLocale === pspCodeLocale
            );

            return dataItem ? dataItem["successRate"] : 0;
          });
        });

        break;
      case "lastWeek":
        const getLabel = getLabelsForLast7Days();
        for (let i = 0; i < getLabel.length; i++) {
          const dataPoint = getLabel[i];
          newLabels.push(dataPoint);
        }
        selectedOptions.forEach((pspCodeLocale) => {
          newDataByPSP[pspCodeLocale] = newLabels.map((dateLabel) => {
            const day = dateLabel.split(" ")[0].padStart(2, "0");
            const month = monthNames.indexOf(dateLabel.split(" ")[1]) + 1;

            const formattedDay = day.padStart(2, "0");
            const formattedMonth = month.toString().padStart(2, "0");
            const formattedDate = `${formattedDay}/${formattedMonth}`;
            const dataItem = filterData?.find(
              (item) =>
                item?.timestamp.includes(formattedDate) &&
                item?.pspCodeLocale === pspCodeLocale
            );

            if (dataItem) {
              return dataItem["successRate"];
            } else {
              return 0;
            }
          });
        });

        break;
      case "thisLastMonth":
        const getLabelForLastMonth = getLabelsForCurrentMonth();
        for (let i = 0; i < getLabelForLastMonth.length; i++) {
          const dataPoint = getLabelForLastMonth[i];
          newLabels.push(dataPoint);
        }
        const todayDateForMonth = dayjs(); // Current date

        selectedOptions.forEach((pspCodeLocale) => {
          newDataByPSP[pspCodeLocale] = newLabels.map((dateLabel) => {
            const day = dateLabel.split(" ")[0].padStart(2, "0");
            const month = monthNames.indexOf(dateLabel.split(" ")[1]) + 1;
            const year = todayDateForMonth.year(); // Get current year

            const formattedDate = dayjs(`${year}-${month}-${day}`, "YYYY-M-D");

            if (formattedDate.isAfter(todayDateForMonth, "day")) {
              return null; // Data is null for future dates
            }

            const formattedDay = formattedDate.format("DD/MM");
            const dataItem = filterData?.find(
              (item) =>
                item?.timestamp.includes(formattedDay) &&
                item?.pspCodeLocale === pspCodeLocale
            );

            return dataItem ? dataItem["successRate"] : 0;
          });
        });

        break;
      case "thisMonth":
        const getLabelForMonth = getLabelsForLast30Day();
        for (let i = 0; i < getLabelForMonth.length; i++) {
          const dataPoint = getLabelForMonth[i];
          newLabels.push(dataPoint);
        }
        selectedOptions.forEach((pspCodeLocale) => {
          newDataByPSP[pspCodeLocale] = newLabels.map((dateLabel) => {
            const day = dateLabel.split(" ")[0].padStart(2, "0");
            const month = monthNames.indexOf(dateLabel.split(" ")[1]) + 1;

            const formattedDay = day.padStart(2, "0");
            const formattedMonth = month.toString().padStart(2, "0");
            const formattedDate = `${formattedDay}/${formattedMonth}`;

            const dataItem = filterData?.find(
              (item) =>
                item?.timestamp.includes(formattedDate) &&
                item?.pspCodeLocale === pspCodeLocale
            );

            if (dataItem) {
              return dataItem["successRate"];
            } else {
              return 0;
            }
          });
        });

        break;

      case "thisYear":
        const getLabelForYear = getLabelsForYear();
        getLabelForYear.forEach((dataPoint) => {
          newLabels.push(dataPoint);
        });

        const chartData = [];
        const volumeData = [];
        const dataByMonth = {};
        const currentMonth = dayjs().month();

        filterData.forEach((item) => {
          const timestamp = item.timestamp;
          const dateParts = timestamp.split(" ")[0].split("/");
          const month = parseInt(dateParts[1]) - 1;
          const totalTransactions = item["successRate"];
          if (!dataByMonth[month]) {
            dataByMonth[month] = { totalTransactions: 0 };
          }
          dataByMonth[month].totalTransactions += totalTransactions;
        });

        let firstDataPoint = -1;
        for (let index = 0; index <= 11; index++) {
          const monthData = dataByMonth[index];
          if (firstDataPoint < 0 && monthData) {
            firstDataPoint = index;
          }

          if (index <= currentMonth) {
            chartData.push(monthData ? monthData.totalTransactions : 0);
            volumeData.push(null);
          } else {
            chartData.push(null);
            volumeData.push(null);
          }
        }

        selectedOptions.forEach((pspCodeLocale) => {
          newDataByPSP[pspCodeLocale] = newLabels.map((dateLabel, idx) => {
            const month = monthNames.indexOf(dateLabel) + 1;
            const formattedMonth = month.toString().padStart(2, "0");
            const formattedDate = `${formattedMonth}/${currentYearThisYear}`;

            if (idx <= currentMonth) {
              const dataItem = filterData.find(
                (item) =>
                  item.timestamp.includes(formattedDate) &&
                  item.pspCodeLocale === pspCodeLocale
              );
              return dataItem ? dataItem["successRate"] : 0;
            } else {
              return null;
            }
          });
        });
        break;

      case "last3Months":
        const getLabelForLast3Months = getLabelsForLast3Months();
        getLabelForLast3Months.forEach((dataPoint) => {
          newLabels.push(dataPoint);
        });

        const chartDataFor3Months = [];
        const dataByMonthFor3Months = {};

        filterData.forEach((item) => {
          const monthBasedLabel = monthBasedLabelWithYear(item?.timestamp)
          const totalTransactions = item["successRate"];
          if (!dataByMonthFor3Months[monthBasedLabel]) {
            dataByMonthFor3Months[monthBasedLabel] = { totalTransactions: 0 };
          }
          dataByMonthFor3Months[monthBasedLabel].totalTransactions += totalTransactions;
        });

        Object.keys(dataByMonthFor3Months).map((item) => {
          const monthData = dataByMonthFor3Months[item];
          chartDataFor3Months.push(monthData ? monthData.totalTransactions : 0);
        });

        selectedOptions.forEach((pspCodeLocale) => {
          newDataByPSP[pspCodeLocale] = newLabels.map((dateLabel, idx) => {
            const month = monthNames.indexOf(dateLabel.split(' ')[0]) + 1;
            const year = dateLabel.split(' ')[1];
            const formattedMonth = month.toString().padStart(2, "0");
            const formattedDate = `${formattedMonth}/${year}`;
            const dataItem = filterData.find(
              (item) =>
                item.timestamp.includes(formattedDate) &&
                item.pspCodeLocale === pspCodeLocale
            );
            return dataItem ? dataItem["successRate"] : 0;
          });
        });
        break;

      case "last6Months":
        const getLabelForLast6Months = getLabelsForLast6Months();
        getLabelForLast6Months.forEach((dataPoint) => {
          newLabels.push(dataPoint);
        });

        const chartDataFor6Months = [];
        const dataByMonthFor6Months = {};

        filterData.forEach((item) => {
          const monthBasedLabel = monthBasedLabelWithYear(item?.timestamp)
          const totalTransactions = item["successRate"];
          if (!dataByMonthFor6Months[monthBasedLabel]) {
            dataByMonthFor6Months[monthBasedLabel] = { totalTransactions: 0 };
          }
          dataByMonthFor6Months[monthBasedLabel].totalTransactions += totalTransactions;
        });

        Object.keys(dataByMonthFor6Months).map((item) => {
          const monthData = dataByMonthFor6Months[item];
          chartDataFor6Months.push(monthData ? monthData.totalTransactions : 0);
        });

        selectedOptions.forEach((pspCodeLocale) => {
          newDataByPSP[pspCodeLocale] = newLabels.map((dateLabel, idx) => {
            const month = monthNames.indexOf(dateLabel.split(' ')[0]) + 1;
            const year = dateLabel.split(' ')[1];
            const formattedMonth = month.toString().padStart(2, "0");
            const formattedDate = `${formattedMonth}/${year}`;
            const dataItem = filterData.find(
              (item) =>
                item.timestamp.includes(formattedDate) &&
                item.pspCodeLocale === pspCodeLocale
            );
            return dataItem ? dataItem["successRate"] : 0;
          });
        });
        break;

      case "last12Months":
        const getLabelForLast12Months = getLabelsForLast12Months();
        getLabelForLast12Months.forEach((dataPoint) => {
          newLabels.push(dataPoint);
        });

        const chartDataFor12Months = [];
        const dataByMonthFor12Months = {};

        filterData.forEach((item) => {
          const monthBasedLabel = monthBasedLabelWithYear(item?.timestamp)
          const totalTransactions = item["successRate"];
          if (!dataByMonthFor12Months[monthBasedLabel]) {
            dataByMonthFor12Months[monthBasedLabel] = { totalTransactions: 0 };
          }
          dataByMonthFor12Months[monthBasedLabel].totalTransactions += totalTransactions;
        });

        Object.keys(dataByMonthFor12Months).map((item) => {
          const monthData = dataByMonthFor12Months[item];
          chartDataFor12Months.push(monthData ? monthData.totalTransactions : 0);
        });

        selectedOptions.forEach((pspCodeLocale) => {
          newDataByPSP[pspCodeLocale] = newLabels.map((dateLabel, idx) => {
            const month = monthNames.indexOf(dateLabel.split(' ')[0]) + 1;
            const year = dateLabel.split(' ')[1];
            const formattedMonth = month.toString().padStart(2, "0");
            const formattedDate = `${formattedMonth}/${year}`;
            const dataItem = filterData.find(
              (item) =>
                item.timestamp.includes(formattedDate) &&
                item.pspCodeLocale === pspCodeLocale
            );
            return dataItem ? dataItem["successRate"] : 0;
          });
        });
        break;

      case "customDate":
        if (singleStartDate) {
          const getLabelForCustomDate = timeLabelForCharts();
          const customDateObj = dayjs(singleStartDate);
          const isTodayCustom = customDateObj.isSame(dayjs(), "day");
          const comparisonTime = isTodayCustom
            ? dayjs()
            : customDateObj.endOf("day");

          getLabelForCustomDate.forEach((dataPoint) => {
            newLabels.push(dataPoint);
            const [labelHour, labelMinute] = dataPoint.split(":").map(Number);
            const pastTime = isTodayCustom
              ? comparisonTime.hour() > labelHour ||
              (comparisonTime.hour() === labelHour &&
                comparisonTime.minute() >= labelMinute)
              : true;

            if (pastTime) {
              pushDataByTimeLabel(
                newLabels,
                newDataByPSP,
                filterData,
                selectedOptions,
                dataPoint,
                comparisonTime.hour(),
                comparisonTime.minute(),
                isTodayCustom,
                dataPoint ===
                getLabelForCustomDate[getLabelForCustomDate.length - 1],
                "successRate"
              );
            } else {
              selectedOptions.forEach((pspCodeLocale) => {
                if (!newDataByPSP[pspCodeLocale]) {
                  newDataByPSP[pspCodeLocale] = [];
                }
                newDataByPSP[pspCodeLocale].push(null);
              });
            }
          });
        } else if (startDate && endDate) {
          const startDateObj = dayjs(startDate);
          const endDateObj = dayjs(endDate);
          const isSameDate = startDateObj.isSame(endDateObj, "day");
          const isToday = startDateObj.isSame(dayjs(), "day");

          if (isSameDate) {
            const getLabelForCustomDate = timeLabelForCharts();
            const comparisonTime = isToday ? dayjs() : endDateObj.endOf("day");

            getLabelForCustomDate.forEach((dataPoint) => {
              newLabels.push(dataPoint);
              const [labelHour, labelMinute] = dataPoint.split(":").map(Number);
              const pastTime = isToday
                ? comparisonTime.hour() > labelHour ||
                (comparisonTime.hour() === labelHour &&
                  comparisonTime.minute() >= labelMinute)
                : true;

              if (pastTime) {
                pushDataByTimeLabel(
                  newLabels,
                  newDataByPSP,
                  filterData,
                  selectedOptions,
                  dataPoint,
                  comparisonTime.hour(),
                  comparisonTime.minute(),
                  isToday,
                  dataPoint ===
                  getLabelForCustomDate[getLabelForCustomDate.length - 1],
                  "successRate"
                );
              } else {
                selectedOptions.forEach((pspCodeLocale) => {
                  if (!newDataByPSP[pspCodeLocale]) {
                    newDataByPSP[pspCodeLocale] = [];
                  }
                  newDataByPSP[pspCodeLocale].push(null);
                });
              }
            });
          } else {
            const dateDiff = endDateObj.diff(startDateObj, "day");

            if (dateDiff <= 62) {
              const getLabelForCustomDateRange = getLabelsForDateRange(
                startDateObj,
                endDateObj
              );
              newLabels = getLabelForCustomDateRange;

              const preprocessedData = preprocessData(filterData);
              const aggregatedData = aggregateData(preprocessedData);

              selectedOptions.forEach((pspCodeLocale) => {
                newDataByPSP[pspCodeLocale] = newLabels.map((dateLabel) => {
                  const [day, monthName] = dateLabel.split(" ");
                  const month = monthNames.indexOf(monthName) + 1;
                  const year = startDateObj.year();
                  const formattedDate = dayjs(
                    `${year}-${month}-${day}`,
                    "YYYY-M-D"
                  ).format("YYYY-MM-DD");

                  const key = `${formattedDate}-${pspCodeLocale}`;
                  const dataItem = aggregatedData[key];
                  return dataItem ? dataItem.successRateSum : 0;
                });
              });
            }
          }
        } else {
          const cachedData =
            JSON.parse(localStorage.getItem("chartData")) || {};
          newLabels = cachedData.labels || [];
          newDataByPSP = cachedData.dataByPSP || {};
        }
        setLabels(newLabels);
        setDataByPSP(newDataByPSP);
        break;

      default:
        break;
    }

    setLabels(newLabels);
    setDataByPSP(newDataByPSP);
  }, [
    selectedDropdownValue,
    singleStartDate,
    filterData,
    selectedOptions,
    currentHour,
    currentMinute,
    startDate,
    endDate,
  ]);

  const defaultColor = "lightgray";

  const datasets = Object.keys(dataByPSP).map((pspCodeLocale) => {
    const matchingData = filteredDataWithColors.find(
      (item) => item.pspCodeLocale === pspCodeLocale
    );

    return {
      label: pspCodeLocale,
      data: dataByPSP[pspCodeLocale],
      type: "line",
      fill: false,
      borderColor: matchingData ? matchingData.color : defaultColor,
      backgroundColor: matchingData ? matchingData.color : defaultColor,
      pointBorderColor: matchingData ? matchingData.color : defaultColor,
      pointBackgroundColor: matchingData ? matchingData.color : defaultColor,
      pointHoverBackgroundColor: matchingData
        ? matchingData.color
        : defaultColor,
      pointHoverBorderColor: matchingData ? matchingData.color : defaultColor,
    };
  });

  const data = {
    labels: labels,
    datasets: datasets,
  };

  const options = {
    layout: {
      padding: {
        bottom: 2,
      },
    },
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          usePointStyle: true,
        },
      },
    },
    scales: {
      x: {
        offset: true,
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: "Poppins",
          },
        },
        title: {
          display: true,
          text: "",
          color: "#6c6c6c",
          font: {
            family: "Poppins",
            size: "12px",
            fontSize: "12px",
            style: "normal",
            weight: "400",
          },
        },
      },
      y: {
        suggestedMin: 0,
        suggestedMax: 100,
        beginAtZero: true,
        grid: {
          borderDash: [8, 6],
        },
        title: {
          display: true,
          text: "Percentage",
          color: "#6c6c6c",
          font: {
            family: "Poppins",
            size: "12px",
            fontSize: "12px",
            style: "normal",
            weight: "400",
          },
        },
        ticks: {
          stepSize: 10,
          callback: (value) => value + "%",
        },
      },
    },
    elements: {
      line: {
        tension: 0,
        backgroundColor: "#CAC9FB",
        borderColor: "#CAC9FB",
        hoverRadius: 4,
        hoverBackgroundColor: "#CAC9FB",
        hoverBorderColor: "#CAC9FB",
      },
      point: {
        radius: 2,
        backgroundColor: "#4F4DC3",
        borderColor: "#4F4DC3",
        borderWidth: 2,
        hoverRadius: 4,
        hoverBackgroundColor: "#4F4DC3",
        hoverBorderColor: "#4F4DC3",
      },
    },
  };

  return (
    <div style={{ height: "340px", width: "100%" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <p className="sales-chart-header">
          Overall Success Rate -{" "}
          {successRatePercentage.length !== 0
            ? successRatePercentage.map(
              (item, idx) => `${item["successRate"]}%`
            )
            : "0%"}
        </p>

        <SelectDropdownCompo
          value={selectedOptions}
          onChange={handleProcessorSelect(
            selectedOptions,
            uniqueOptionTypes,
            selectAll
          )}
          width="160px"
          options={gmvPspData}
          label="Processors"
          selectAll={selectAll}
        />
      </div>
      <Line data={data} options={options} />
    </div>
  );
};

export default SuccessChart;
